import { useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, LinearProgress, ListItemIcon, Menu, MenuItem, Radio, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import FigmaStimulusEditor, { FIGMA_DEFAULT_LIMIT_MS } from './FigmaStimulusEditor'
import { useEdition } from '@/hooks/useEdition'
import { youTubeId, youTubeThumb } from '@/utils/youtube'
import { EYE_TRACKING_NEW_STUDIES_ENABLED } from '@/config/featureFlags'
import { CALIBRATION_VARIANTS } from '@/lib/calibrationVariants'
import { videoFileDurationSeconds } from './basicStudy'
import { DeskInput, Group, Kbd, Row, StepHeading, SwitchRow, Tag } from './desk'
import { LINE, primaryBtnSx } from './deskStyles'
import { formatDuration, stimulusSeconds } from './estimate'
import type { DraftPatch, StimulusDraft, StimulusType, StudyDraft } from './types'

type StepProps = {
  draft: StudyDraft
  onChange: (patch: DraftPatch) => void
  /** Patch one stimulus against the latest draft — for results that arrive
   *  after an await (a measured video length), when `draft` may be stale. */
  onPatchStimulus: (id: string, patch: Partial<StimulusDraft>) => void
}

const TYPE_ICON: Record<StimulusType, SvgIconComponent> = {
  video: VideocamOutlinedIcon,
  image: ImageOutlinedIcon,
  audio: MicNoneOutlinedIcon,
  text: TitleOutlinedIcon,
  url: LinkOutlinedIcon,
  figma: TouchAppOutlinedIcon,
}

const TYPE_COLOR: Record<StimulusType, string> = {
  video: '#EA5048',
  image: '#3B82F6',
  audio: '#8B5CF6',
  text: '#6B7280',
  url: '#10B981',
  figma: '#1B1C38',
}

/** One letter per type, active while the "Dodaj" menu is open. */
const SHORTCUT: Record<StimulusType, string> = { video: 'V', image: 'I', audio: 'A', text: 'T', url: 'L', figma: 'F' }

const ADD_TYPES: StimulusType[] = ['video', 'image', 'audio', 'text', 'url']
// A clickable Figma prototype is Pro (Z-98) and hidden — not locked — for
// FaceWise (decision 2026-09-23). The backend refuses it on its own as well.
const PRO_ADD_TYPES: StimulusType[] = [...ADD_TYPES, 'figma']

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** What a row still lacks before it can be shown to anybody, if anything. */
function missing(s: StimulusDraft): 'text' | 'url' | null {
  if (s.type === 'text' && !s.payload?.trim()) return 'text'
  if ((s.type === 'url' || s.type === 'figma') && !s.url?.trim()) return 'url'
  return null
}

export default function WizardStepStimulus({ draft, onChange, onPatchStimulus }: StepProps) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const { isPro } = useEdition()
  const addTypes = isPro ? PRO_ADD_TYPES : ADD_TYPES

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingTypeRef = useRef<StimulusType>('video')
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const [dropError, setDropError] = useState('')

  // Drag-to-reorder state (native HTML5 DnD, started from the grip handle).
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  const reorder = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return
    const next = [...draft.stimuli]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    onChange({ stimuli: next })
  }

  const updateStimulus = (id: string, patch: Partial<StimulusDraft>) =>
    onChange({ stimuli: draft.stimuli.map((s) => (s.id === id ? { ...s, ...patch } : s)) })
  const removeStimulus = (id: string) => onChange({ stimuli: draft.stimuli.filter((s) => s.id !== id) })

  // Local object-URL previews for picked files (revoked on change/unmount).
  const [previews, setPreviews] = useState<Record<string, string>>({})
  useEffect(() => {
    const map: Record<string, string> = {}
    for (const s of draft.stimuli) if (s.file) map[s.id] = URL.createObjectURL(s.file)
    setPreviews(map)
    return () => Object.values(map).forEach((u) => URL.revokeObjectURL(u))
  }, [draft.stimuli])

  /** Add picked or dropped files; the length of a video is read afterwards. */
  const addFiles = (files: File[], forcedType?: StimulusType) => {
    const added: StimulusDraft[] = []
    for (const file of files) {
      const type: StimulusType | null =
        forcedType ?? (file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'image' : null)
      if (type !== 'video' && type !== 'image') continue
      added.push({
        id: `stim-${Date.now()}-${added.length}-${type}`,
        type,
        name: file.name,
        label: `#${draft.stimuli.length + added.length + 1}`,
        meta: formatBytes(file.size),
        status: 'ready',
        file,
        ...(type === 'image' ? { exposureMs: 5000 } : {}),
      })
    }
    if (added.length < files.length) setDropError(t('wizard.desk.stimulus.dropUnsupported'))
    if (added.length === 0) return
    onChange({ stimuli: [...draft.stimuli, ...added] })
    for (const s of added) {
      if (s.type !== 'video' || !s.file) continue
      void videoFileDurationSeconds(s.file).then((seconds) => {
        if (seconds != null) onPatchStimulus(s.id, { durationSeconds: seconds })
      })
    }
  }

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (file) addFiles([file], pendingTypeRef.current)
  }

  const addStimulus = (type: StimulusType) => {
    setMenuAnchor(null)
    setDropError('')
    const base = { id: `stim-${Date.now()}-${type}`, label: `#${draft.stimuli.length + 1}`, status: 'ready' as const }
    if (type === 'video' || type === 'image') {
      pendingTypeRef.current = type
      if (fileInputRef.current) {
        fileInputRef.current.accept = type === 'video' ? 'video/*' : 'image/*'
        fileInputRef.current.click()
      }
      return
    }
    const item: StimulusDraft =
      type === 'url'
        ? { ...base, type, name: '', meta: t('wizard.stimulus.urlMeta'), url: '' }
        : type === 'figma'
          ? { ...base, type, name: '', meta: t('wizard.stimulus.types.figma.sub'), url: '', payload: '', goalNodeId: '', exposureMs: FIGMA_DEFAULT_LIMIT_MS }
          : type === 'text'
            ? { ...base, type, name: `${t('wizard.stimulus.types.text.label')} ${draft.stimuli.length + 1}`, meta: t('wizard.stimulus.types.text.sub'), payload: '', exposureMs: 5000 }
            : // audio — placeholder only (not persisted)
              { ...base, type, name: `${t(`wizard.stimulus.types.${type}.label`)} ${draft.stimuli.length + 1}`, meta: t(`wizard.stimulus.types.${type}.sub`) }
    onChange({ stimuli: [...draft.stimuli, item] })
  }

  const thumb = (stim: StimulusDraft) => {
    const preview = previews[stim.id]
    const ytId = stim.type === 'url' ? youTubeId(stim.url) : null
    const imageSrc = preview && stim.type === 'image' ? preview : ytId ? youTubeThumb(ytId) : null
    const videoSrc = preview && stim.type === 'video' ? preview : null
    const Icon = TYPE_ICON[stim.type]
    return (
      <Box
        sx={{
          width: 64,
          height: 40,
          borderRadius: '8px',
          flexShrink: 0,
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          bgcolor: imageSrc || videoSrc ? '#000' : stim.type === 'video' ? '#1B1C38' : '#F0F0F4',
        }}
      >
        {imageSrc ? (
          <Box component="img" src={imageSrc} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : videoSrc ? (
          <Box component="video" src={`${videoSrc}#t=0.1`} muted playsInline preload="metadata" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Icon sx={{ fontSize: 18, color: stim.type === 'video' ? '#fff' : c.ink56 }} />
        )}
      </Box>
    )
  }

  /** Length as text for the row's second line; null when unknown. */
  const lengthText = (stim: StimulusDraft): string | null => {
    if (stim.type === 'image' || stim.type === 'text') return formatDuration(Math.round((stim.exposureMs ?? 5000) / 1000))
    const seconds = stimulusSeconds(stim)
    if (seconds != null) return formatDuration(seconds)
    if (stim.type === 'figma' && stim.exposureMs) return t('wizard.desk.stimulus.upTo', { minutes: Math.round(stim.exposureMs / 60000) })
    return null
  }

  /** How long an image or a text stays on screen. */
  const exposureField = (stim: StimulusDraft) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Typography component="span" sx={{ fontSize: 13.5, color: c.ink72 }}>
        {t('wizard.stimulus.exposureLabel')}
      </Typography>
      <DeskInput
        type="number"
        value={Math.round((stim.exposureMs ?? 5000) / 1000)}
        onChange={(e) => {
          const secs = Math.max(1, Math.min(600, Math.round(Number(e.target.value) || 0)))
          updateStimulus(stim.id, { exposureMs: secs * 1000 })
        }}
        suffix={t('wizard.stimulus.exposureUnit')}
        slotProps={{ htmlInput: { min: 1, max: 600, 'aria-label': t('wizard.stimulus.exposureLabel') } }}
        sx={{ width: 112, '& .MuiOutlinedInput-root': { height: 36, fontSize: 14 } }}
      />
    </Box>
  )

  const statusCell = (stim: StimulusDraft) => {
    if (stim.status === 'uploading') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, color: c.ink56, whiteSpace: 'nowrap' }}>
          {t('wizard.desk.stimulus.uploading', { progress: stim.progress ?? 0 })}
          <LinearProgress
            variant="determinate"
            value={stim.progress ?? 0}
            sx={{ width: 60, height: 4, borderRadius: 9, bgcolor: '#EEE', '& .MuiLinearProgress-bar': { bgcolor: c.coral } }}
          />
        </Box>
      )
    }
    if (stim.status === 'error') return <Tag tone="danger">{t('wizard.desk.stimulus.failed')}</Tag>
    const lack = missing(stim)
    if (lack) return <Tag tone="warn">{t(lack === 'text' ? 'wizard.desk.stimulus.needsText' : 'wizard.desk.stimulus.needsUrl')}</Tag>
    return <Tag tone="ok">{t('wizard.desk.stimulus.ready')}</Tag>
  }

  /** Editors that do not fit in a cell get a full-width row under their item. */
  const editorRow = (stim: StimulusDraft) => {
    if (stim.type === 'text') {
      return (
        <>
          <DeskInput
            multiline
            minRows={2}
            maxRows={6}
            value={stim.payload ?? ''}
            onChange={(e) => updateStimulus(stim.id, { payload: e.target.value })}
            placeholder={t('wizard.stimulus.textPlaceholder')}
          />
          {exposureField(stim)}
        </>
      )
    }
    if (stim.type === 'image') return exposureField(stim)
    if (stim.type === 'url') {
      return (
        <DeskInput
          value={stim.url ?? ''}
          onChange={(e) => updateStimulus(stim.id, { url: e.target.value, name: e.target.value })}
          placeholder={t('wizard.stimulus.urlPlaceholder')}
          prefix={<LinkOutlinedIcon sx={{ fontSize: 16, color: c.ink48 }} />}
        />
      )
    }
    if (stim.type === 'figma') return <FigmaStimulusEditor stim={stim} onChange={(patch) => updateStimulus(stim.id, patch)} />
    return null
  }

  const rowName = (stim: StimulusDraft) => {
    if (stim.type === 'url') return stim.url?.trim() || t('wizard.stimulus.types.url.label')
    if (stim.type === 'figma') return stim.payload?.trim() || t('wizard.stimulus.types.figma.label')
    return stim.name
  }

  const addButton = (
    <Button
      variant="contained"
      onClick={(e) => setMenuAnchor(e.currentTarget)}
      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
      sx={primaryBtnSx}
      aria-haspopup="menu"
    >
      {t('wizard.desk.stimulus.add')}
    </Button>
  )

  return (
    <>
      <StepHeading title={t('wizard.desk.stimulus.title')} lead={t('wizard.desk.stimulus.lead')} />
      <input ref={fileInputRef} type="file" hidden onChange={handleFilePicked} />

      <Group
        title={t('wizard.desk.stimulus.materials')}
        badge={draft.stimuli.length > 0 ? <Tag>{draft.stimuli.length}</Tag> : undefined}
        action={addButton}
        flush
      >
        {draft.stimuli.map((stim, i) => {
          const editor = editorRow(stim)
          const isOver = overIndex === i && dragIndex !== null && dragIndex !== i
          const length = lengthText(stim)
          const metaParts = [t(`wizard.stimulus.types.${stim.type}.label`), length, stim.type !== 'figma' && stim.type !== 'text' ? stim.meta : null].filter(Boolean)
          return (
            <Row
              key={stim.id}
              sx={{
                opacity: dragIndex === i ? 0.45 : 1,
                boxShadow: isOver ? `inset 0 2px 0 ${c.coral}` : 'none',
              }}
            >
              <Box
                onDragOver={(e: React.DragEvent) => {
                  if (dragIndex === null) return
                  e.preventDefault()
                  if (overIndex !== i) setOverIndex(i)
                }}
                onDrop={(e: React.DragEvent) => {
                  if (dragIndex === null) return
                  e.preventDefault()
                  reorder(dragIndex, i)
                  setDragIndex(null)
                  setOverIndex(null)
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
              >
                <Box
                  draggable
                  onDragStart={(e: React.DragEvent) => {
                    setDragIndex(i)
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', String(i)) // Firefox needs a payload
                  }}
                  onDragEnd={() => {
                    setDragIndex(null)
                    setOverIndex(null)
                  }}
                  aria-label={t('wizard.stimulus.reorder')}
                  title={t('wizard.stimulus.reorder')}
                  sx={{ display: 'flex', cursor: 'grab', ml: -0.75, '&:active': { cursor: 'grabbing' } }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 18, color: c.ink32 }} />
                </Box>
                {thumb(stim)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {rowName(stim)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: c.ink56, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
                    {metaParts.join(' · ')}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0 }}>{statusCell(stim)}</Box>
                <IconButton
                  size="small"
                  aria-label={t('wizard.stimulus.delete')}
                  title={t('wizard.stimulus.delete')}
                  onClick={() => removeStimulus(stim.id)}
                  sx={{ color: c.ink48, flexShrink: 0, mr: -0.75, '&:hover': { color: c.danger } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Box>
              {editor && <Box sx={{ mt: 1.75, pl: { sm: 'calc(18px + 64px + 24px - 6px)' }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>{editor}</Box>}
            </Row>
          )
        })}

        {/* Files can be dropped straight onto the list; the type is read from the file. */}
        <Row>
          <Box
            onDragOver={(e) => {
              if (dragIndex !== null || !e.dataTransfer.types.includes('Files')) return
              e.preventDefault()
              setDropActive(true)
            }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(e) => {
              if (dragIndex !== null) return
              e.preventDefault()
              setDropActive(false)
              setDropError('')
              addFiles(Array.from(e.dataTransfer.files))
            }}
            sx={{
              px: 2,
              py: draft.stimuli.length ? 1.75 : 5,
              border: `1.5px dashed ${dropActive ? c.coral : '#D4D4DC'}`,
              bgcolor: dropActive ? c.coralSoft : '#FAFAFC',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: draft.stimuli.length ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: draft.stimuli.length ? 1.25 : 1,
              color: c.ink56,
              fontSize: 13.5,
              transition: 'background-color .15s ease, border-color .15s ease',
            }}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: draft.stimuli.length ? 18 : 26, color: c.ink48 }} />
            <Box component="span" sx={{ maxWidth: 440, lineHeight: 1.45 }}>
              {draft.stimuli.length ? t('wizard.desk.stimulus.dropMore') : t('wizard.desk.stimulus.dropFirst')}
            </Box>
          </Box>
          {dropError && <Typography sx={{ color: c.danger, fontSize: 13, mt: 1 }}>{dropError}</Typography>}
        </Row>
      </Group>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onKeyDown={(e) => {
          const type = addTypes.find((ty) => SHORTCUT[ty] === e.key.toUpperCase())
          if (type && !e.metaKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault()
            addStimulus(type)
          }
        }}
        slotProps={{ paper: { sx: { mt: 0.75, width: 300, borderRadius: '12px', border: `1px solid ${LINE}`, boxShadow: '0 12px 32px -8px rgba(27,28,56,.25)' } } }}
      >
        {addTypes.map((type) => {
          const Icon = TYPE_ICON[type]
          return (
            <MenuItem key={type} onClick={() => addStimulus(type)} sx={{ gap: 1.25, py: 0.875, mx: 0.625, borderRadius: '6px' }}>
              <ListItemIcon sx={{ minWidth: 0 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '6px', bgcolor: TYPE_COLOR[type], display: 'grid', placeItems: 'center' }}>
                  <Icon sx={{ fontSize: 14, color: '#fff' }} />
                </Box>
              </ListItemIcon>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5 }}>{t(`wizard.stimulus.types.${type}.label`)}</Typography>
                <Typography sx={{ fontSize: 12, color: c.ink48 }}>{t(`wizard.stimulus.types.${type}.sub`)}</Typography>
              </Box>
              <Kbd>{SHORTCUT[type]}</Kbd>
            </MenuItem>
          )
        })}
      </Menu>

      {/* Eye tracking opt-in — adds a gaze calibration to the respondent flow
          and makes the analysis compute a gaze point per frame. Gated by
          EYE_TRACKING_NEW_STUDIES_ENABLED: when off, the control is hidden for
          new studies but kept while a study that already has it is edited, so
          the setting survives (the backend mirrors this). */}
      {(EYE_TRACKING_NEW_STUDIES_ENABLED || draft.eyeTracking) && (
        <Group title={t('wizard.desk.stimulus.etTitle')} badge={<Tag tone="coral">beta</Tag>} description={t('wizard.desk.stimulus.etDesc')} flush>
          <SwitchRow checked={draft.eyeTracking} onChange={(eyeTracking) => onChange({ eyeTracking })} title={t('wizard.desk.stimulus.etToggle')}>
            {/* Calibration variant (Z-45): which fixed protocol the respondent's
                calibration runs. Hard-coded list, decision 2026-09-09. */}
            {draft.eyeTracking && (
              <Box role="radiogroup" aria-label={t('wizard.stimulus.calibration.title')} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                {CALIBRATION_VARIANTS.map((variant) => {
                  const selected = draft.calibrationVariant === variant
                  return (
                    <Box
                      key={variant}
                      component="label"
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                        p: 1.5,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: `1px solid ${selected ? c.coral : LINE}`,
                        boxShadow: selected ? `0 0 0 3px ${c.coralSoft}` : 'none',
                        transition: 'border-color .15s ease, box-shadow .15s ease',
                      }}
                    >
                      <Radio size="small" checked={selected} onChange={() => onChange({ calibrationVariant: variant })} sx={{ p: 0, mt: '1px' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t(`wizard.desk.stimulus.calibration.${variant}.label`)}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>{t(`wizard.desk.stimulus.calibration.${variant}.meta`)}</Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </SwitchRow>
        </Group>
      )}
    </>
  )
}
